import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import NativeSubscribeButton from './NativeSubscribeButton';
import { isNativePlatform } from '../lib/native';
import { formatPrice, useStorePrice } from '../lib/price';
import {
  trackPremiumCancellationRequested,
  trackPremiumCancellationReversed,
} from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateLong = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};

const SubscriptionSection = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const inFlight = useRef(false);
  const storePrice = useStorePrice(
    status?.subscription_source === 'apple_iap' || status?.subscription_source === 'google_play',
    user
  );

  const fetchStatus = useCallback(async () => {
    try {
      setLoadError(false);
      const res = await fetch(`${API_URL}/api/subscription/status`, {
        credentials: 'include',
      });
      if (!res.ok) {
        setLoadError(true);
        return;
      }
      const data = await res.json();
      setStatus(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const doCancel = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/subscription/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        if (res.status === 404) {
          toast.error('We could not find an active subscription for this account.');
        } else {
          toast.error('We could not cancel your subscription. Please try again.');
        }
        return;
      }
      const data = await res.json();
      // Analytics — non-sensitive properties only
      trackPremiumCancellationRequested({
        source: 'settings',
        plan: 'premium',
        cancellation_effective_date: (data.premium_access_until || '').slice(0, 10),
        subscription_status: data.subscription_status || 'active',
      });
      const dateStr = formatDateLong(data.premium_access_until);
      toast.success(
        dateStr
          ? `Your subscription has been cancelled. You will keep Premium access until ${dateStr}.`
          : 'Your subscription has been cancelled. You will keep Premium access until the end of the current billing period.'
      );
      setCancelOpen(false);
      // Refresh both subscription status and global user (in case flags updated)
      await Promise.all([fetchStatus(), checkAuth()]);
    } catch {
      toast.error('We could not cancel your subscription. Please try again.');
    } finally {
      setProcessing(false);
      inFlight.current = false;
    }
  };

  const doResume = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/subscription/resume`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        if (res.status === 404) {
          toast.error('We could not find an active subscription for this account.');
        } else {
          toast.error('We could not update your subscription. Please try again.');
        }
        return;
      }
      const data = await res.json();
      trackPremiumCancellationReversed({
        source: 'settings',
        plan: 'premium',
        subscription_status: data.subscription_status || 'active',
      });
      toast.success('Your Premium subscription will continue.');
      setResumeOpen(false);
      await Promise.all([fetchStatus(), checkAuth()]);
    } catch {
      toast.error('We could not update your subscription. Please try again.');
    } finally {
      setProcessing(false);
      inFlight.current = false;
    }
  };

  // ---------- Loading skeleton ----------
  if (loading) {
    return (
      <section
        aria-label="Future Self Premium"
        className="bg-[#252528] border border-white/5 rounded-[24px] p-6"
        data-testid="subscription-section-loading"
      >
        <div className="flex items-center gap-2 mb-5">
          <Crown className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
          <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">
            Future Self Premium
          </span>
        </div>
        <div className="space-y-3 animate-pulse" aria-hidden="true">
          <div className="h-4 w-40 bg-white/5 rounded" />
          <div className="h-4 w-32 bg-white/5 rounded" />
          <div className="h-4 w-48 bg-white/5 rounded" />
          <div className="h-9 w-full sm:w-56 bg-white/5 rounded-full mt-4" />
        </div>
      </section>
    );
  }

  // ---------- Error state ----------
  if (loadError) {
    return (
      <section
        aria-label="Future Self Premium"
        className="bg-[#252528] border border-white/5 rounded-[24px] p-6"
        data-testid="subscription-section-error"
      >
        <div className="flex items-center gap-2 mb-4">
          <Crown className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
          <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">
            Future Self Premium
          </span>
        </div>
        <p className="text-[#DCD3CA] font-sans text-sm mb-4">
          Your subscription status could not be loaded.
        </p>
        <button
          onClick={fetchStatus}
          className="text-[#8BAA9E] text-sm font-sans underline underline-offset-4 hover:text-[#DCD3CA] transition-colors"
          data-testid="subscription-retry-btn"
        >
          Try again
        </button>
      </section>
    );
  }

  const isPremium = !!status?.is_premium;
  const scheduledCancel = !!status?.cancel_at_period_end;
  const periodEndDate = formatDateLong(status?.current_period_end);
  // Apple/Google subscriber who turned off auto-renew: show when access ends.
  const iapEnding =
    status?.subscription_source === 'apple_iap' || status?.subscription_source === 'google_play'
      ? status?.will_renew === false && !!periodEndDate
      : false;
  const subscriptionSource = status?.subscription_source; // 'stripe' | 'apple_iap' | 'google_play' | null
  const isNativeManaged = subscriptionSource === 'apple_iap' || subscriptionSource === 'google_play';
  const isAdminGranted =
    isPremium && !isNativeManaged && subscriptionSource !== 'stripe' && !status?.stripe_subscription_id_exists;
  // Only show a price we actually know: Stripe subscription item or store price.
  const priceLabel = isNativeManaged
    ? storePrice
    : formatPrice(status?.price_amount, status?.price_currency);

  // ---------- FREE plan ----------
  if (!isPremium) {
    return (
      <section
        aria-label="Future Self Premium"
        className="bg-[#252528] border border-white/5 rounded-[24px] p-6"
        data-testid="subscription-section-free"
      >
        <div className="flex items-center gap-2 mb-4">
          <Crown className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
          <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">
            Future Self Premium
          </span>
        </div>
        <dl className="space-y-2 mb-5">
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-[#7A7670] font-sans text-sm">Plan</dt>
            <dd
              className="font-serif text-lg text-[#DCD3CA]"
              data-testid="subscription-plan-free"
            >
              Free
            </dd>
          </div>
        </dl>
        {isNativePlatform() ? (
          <NativeSubscribeButton
            isPremium={false}
            subscriptionSource={subscriptionSource}
            onRefresh={fetchStatus}
          />
        ) : (
          <button
            onClick={() => navigate('/premium')}
            className="w-full bg-gradient-to-r from-[#C6A75E]/20 to-[#C6A75E]/10 border border-[#C6A75E]/30 rounded-full px-5 py-3 flex items-center justify-center gap-2 text-[#C6A75E] font-sans text-sm hover:brightness-110 transition"
            data-testid="upgrade-premium-btn"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            Upgrade to Premium
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </section>
    );
  }

  // ---------- PREMIUM: active + not cancelling ----------
  if (isPremium && !scheduledCancel) {
    return (
      <>
        <section
          aria-label="Future Self Premium"
          className="bg-[#252528] border border-white/5 rounded-[24px] p-6"
          data-testid="subscription-section-active"
        >
          <div className="flex items-center gap-2 mb-5">
            <Crown className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
            <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">
              Future Self Premium
            </span>
          </div>
          <dl className="space-y-3 mb-5">
            <Row
              label="Plan"
              value={isAdminGranted ? 'Premium Access — Complimentary' : 'Future Self Premium'}
              testId="sub-plan"
            />
            {!isAdminGranted && priceLabel && (
              <Row label="Price" value={`${priceLabel} per month`} testId="sub-price" />
            )}
            <Row label="Status" value={iapEnding ? 'Active — auto-renew off' : 'Active'} testId="sub-status" />
            {periodEndDate && (
              <Row
                label={iapEnding ? 'Premium access ends' : 'Next billing date'}
                value={periodEndDate}
                testId={iapEnding ? 'sub-access-ends' : 'sub-next-billing'}
              />
            )}
          </dl>
          {iapEnding && (
            <p className="text-[#B5B1AB] text-xs font-sans leading-relaxed mb-5" data-testid="iap-ending-note">
              You've turned off auto-renew. You'll keep Premium until {periodEndDate}.
            </p>
          )}
          {/* Web cancel link only for web-managed (Stripe) subscriptions.
              Native-managed subs must be cancelled in App Store / Play.
              Also require a real stripe_subscription_id — admin-granted
              premium accounts (e.g. the App Store reviewer account) have
              no Stripe subscription to cancel, so the button would hit
              /subscription/cancel → 404. */}
          {!isNativeManaged && status?.stripe_subscription_id_exists && (
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="text-[#7A7670] hover:text-[#B5B1AB] text-xs font-sans underline underline-offset-4 decoration-[#7A7670]/40 hover:decoration-[#B5B1AB]/60 transition-colors focus:outline-none focus:ring-2 focus:ring-[#8BAA9E]/30 focus:ring-offset-2 focus:ring-offset-[#252528] rounded-sm"
              data-testid="cancel-subscription-btn"
              aria-label="Cancel Premium subscription"
            >
              Cancel subscription
            </button>
          )}
          {isAdminGranted && (
            <p
              className="text-[#B5B1AB] text-xs font-sans leading-relaxed"
              data-testid="admin-granted-premium-note"
            >
              Your Premium access was granted directly to this account, so there is no
              subscription to manage or cancel. For assistance, contact{' '}
              <a
                href="mailto:support@futureself.es"
                className="text-[#8BAA9E] underline underline-offset-4 decoration-[#8BAA9E]/40"
                data-testid="admin-granted-support-email"
              >
                support@futureself.es
              </a>
              .
            </p>
          )}
          {/* Native: Restore Purchases + "manage in store" hint */}
          {isNativePlatform() && (
            <div className="mt-4">
              <NativeSubscribeButton
                isPremium={true}
                subscriptionSource={subscriptionSource}
                onRefresh={fetchStatus}
              />
            </div>
          )}
        </section>

        <CancelDialog
          open={cancelOpen}
          onOpenChange={(v) => !processing && setCancelOpen(v)}
          endDateLabel={periodEndDate}
          processing={processing}
          onConfirm={doCancel}
        />
      </>
    );
  }

  // ---------- PREMIUM: scheduled to cancel ----------
  return (
    <>
      <section
        aria-label="Future Self Premium"
        className="bg-[#252528] border border-[#C6A75E]/20 rounded-[24px] p-6"
        data-testid="subscription-section-cancelling"
      >
        <div className="flex items-center gap-2 mb-5">
          <Crown className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
          <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">
            Future Self Premium
          </span>
        </div>
        <dl className="space-y-3 mb-6">
          <Row label="Plan" value="Future Self Premium" testId="sub-plan" />
          <Row
            label="Status"
            value={periodEndDate ? `Cancels on ${periodEndDate}` : 'Cancels at end of period'}
            testId="sub-status"
            valueClass="text-[#C6A75E]"
          />
          {periodEndDate && (
            <Row
              label="Premium access until"
              value={periodEndDate}
              testId="sub-access-until"
            />
          )}
        </dl>
        <Button
          type="button"
          onClick={() => setResumeOpen(true)}
          className="w-full sm:w-auto rounded-full bg-[#8BAA9E] hover:bg-[#8BAA9E]/90 text-[#1C1C1E] font-sans text-sm px-6 py-2.5 h-auto"
          data-testid="keep-premium-btn"
          aria-label="Keep Premium subscription"
        >
          Keep Premium
        </Button>
      </section>

      <ResumeDialog
        open={resumeOpen}
        onOpenChange={(v) => !processing && setResumeOpen(v)}
        endDateLabel={periodEndDate}
        priceLabel={priceLabel}
        processing={processing}
        onConfirm={doResume}
      />
    </>
  );
};

const Row = ({ label, value, testId, valueClass = 'text-[#DCD3CA]' }) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt className="text-[#7A7670] font-sans text-sm shrink-0">{label}</dt>
    <dd
      className={`font-sans text-sm text-right ${valueClass}`}
      data-testid={testId}
    >
      {value}
    </dd>
  </div>
);

const CancelDialog = ({ open, onOpenChange, endDateLabel, processing, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="bg-[#252528] border border-white/10 text-[#DCD3CA] sm:max-w-md rounded-[24px]"
      onEscapeKeyDown={(e) => {
        if (processing) e.preventDefault();
      }}
      onInteractOutside={(e) => {
        if (processing) e.preventDefault();
      }}
      data-testid="cancel-subscription-modal"
    >
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-[#DCD3CA]">
          Cancel Premium?
        </DialogTitle>
        <DialogDescription className="text-[#B5B1AB] font-sans text-sm leading-relaxed pt-2">
          Your Premium access will remain active until{' '}
          <span className="text-[#DCD3CA]">{endDateLabel || 'the end of the current billing period'}</span>.
          You will not be charged again after that date. After your subscription
          ends, your account will return to the free plan.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="ghost"
          disabled={processing}
          onClick={() => onOpenChange(false)}
          className="text-[#DCD3CA] hover:bg-white/5 rounded-full px-5 h-12 font-sans"
          data-testid="cancel-modal-keep-btn"
        >
          Keep Premium
        </Button>
        <Button
          type="button"
          disabled={processing}
          onClick={onConfirm}
          className="rounded-full bg-[#8BAA9E] hover:bg-[#8BAA9E]/90 text-[#1C1C1E] font-sans px-5 h-12"
          data-testid="cancel-modal-confirm-btn"
          aria-busy={processing}
        >
          {processing ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cancelling…
            </span>
          ) : (
            'Confirm cancellation'
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const ResumeDialog = ({ open, onOpenChange, endDateLabel, priceLabel, processing, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="bg-[#252528] border border-white/10 text-[#DCD3CA] sm:max-w-md rounded-[24px]"
      onEscapeKeyDown={(e) => {
        if (processing) e.preventDefault();
      }}
      onInteractOutside={(e) => {
        if (processing) e.preventDefault();
      }}
      data-testid="resume-subscription-modal"
    >
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-[#DCD3CA]">
          Keep Premium?
        </DialogTitle>
        <DialogDescription className="text-[#B5B1AB] font-sans text-sm leading-relaxed pt-2">
          Your Premium subscription will continue and recurring billing
          {priceLabel ? ` at ${priceLabel} per month` : ''} will resume as usual
          {endDateLabel ? `, starting ${endDateLabel}` : ''}.
          You can cancel again anytime.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="ghost"
          disabled={processing}
          onClick={() => onOpenChange(false)}
          className="text-[#DCD3CA] hover:bg-white/5 rounded-full px-5 h-12 font-sans"
          data-testid="resume-modal-back-btn"
        >
          Back
        </Button>
        <Button
          type="button"
          disabled={processing}
          onClick={onConfirm}
          className="rounded-full bg-[#8BAA9E] hover:bg-[#8BAA9E]/90 text-[#1C1C1E] font-sans px-5 h-12"
          data-testid="resume-modal-confirm-btn"
          aria-busy={processing}
        >
          {processing ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Updating…
            </span>
          ) : (
            'Keep Premium'
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default SubscriptionSection;
